/** 01:23.4 for the timeline ruler; hours only when the sequence is long enough. */
export function formatTimecode(sec, fps = 30, withFrames = false) {
    // Count in whole frames from the start. Working in seconds and taking the
    // remainder let a rounding error land the playhead a hair below a frame
    // boundary, and the display then showed the frame before -- at 50 fps,
    // stepping from frame 28 to 29 read "28" twice.
    const total = Math.floor(Math.max(0, sec) * fps + 1e-6);
    const frames = total % fps;
    const whole = (total - frames) / fps;
    const h = Math.floor(whole / 3600);
    const m = Math.floor((whole % 3600) / 60);
    const secs = whole % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(secs).padStart(2, '0');
    const head = h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
    return withFrames ? `${head}:${String(frames).padStart(2, '0')}` : head;
}
/**
 * Alle drei, in der Reihenfolge, in der der Knopf sie durchläuft. **Timecode
 * steht vorn, weil es die Anzeige ist, die die App schon hatte** -- wer nichts
 * umstellt, findet nichts Neues vor.
 */
export const ZEIT_FORMATE = ['timecode', 'uhrzeit', 'bilder'];
/** Wie das Format im Knopf heisst. */
export const ZEIT_FORMAT_NAME = {
    timecode: 'Timecode',
    uhrzeit: 'Uhrzeit',
    bilder: 'Bildnummer',
};
/** Das nächste Format, im Kreis -- der Knopf hat nur eine Richtung. */
export function naechstesZeitFormat(jetzt) {
    const i = ZEIT_FORMATE.indexOf(jetzt);
    return ZEIT_FORMATE[(i + 1) % ZEIT_FORMATE.length];
}
/**
 * Eine Zeit im gewählten Format.
 *
 * `genau` sagt, ob die Stelle das Letzte nach der Sekunde nennen soll -- am
 * Anfasser eines Clips ja, an der Skala der Zeitleiste nein. Was dieses Letzte
 * ist, entscheidet das Format: beim Timecode das Bild, bei der Uhrzeit das
 * Zehntel. Die **Bildnummer** ist von sich aus genau; eine ungefähre
 * Bildnummer wäre keine.
 *
 * `formatZeit(x, fps, 'timecode', genau)` ist absichtlich Zeichen für Zeichen
 * dasselbe wie `formatTimecode(x, fps, genau)` -- so ändert das umschaltbare
 * Format nichts an dem, was ohne Umschalten dasteht.
 *
 * Gezählt wird überall in ganzen Bildern vom Anfang -- dieselbe Rechnung wie
 * in `formatTimecode`, damit die drei Formate nie verschiedene Bilder meinen.
 */
export function formatZeit(sec, fps, format, genau = false) {
    const takt = fps > 0 ? fps : 30;
    if (format === 'bilder')
        return String(Math.floor(Math.max(0, sec) * takt + 1e-6));
    if (format === 'timecode')
        return formatTimecode(sec, takt, genau);
    if (!genau)
        return formatTimecode(sec, takt, false);
    // Uhrzeit und genau: Zehntel. Ein Bild wäre hier die falsche Einheit -- wer
    // Uhrzeit gewählt hat, will keine Bildnummer lesen.
    const total = Math.floor(Math.max(0, sec) * takt + 1e-6);
    const zehntel = Math.floor((total % takt) * (10 / takt));
    return `${formatTimecode(sec, takt, false)}.${zehntel}`;
}
export function snapToFrame(sec, fps) {
    return Math.round(sec * fps) / fps;
}
/**
 * Moves the playhead by whole frames, counted from the frame that is on
 * screen -- `floor`, the same as the timecode uses, so the two can never
 * disagree. Rounding instead would skip: from a playhead 95% of the way
 * through frame 60, rounding first and adding one lands on 62 while the
 * viewer is still looking at 60.
 *
 * The result is always exactly on the frame grid. Adding 1/fps to wherever
 * playback happened to stop also advances one frame, but leaves the playhead
 * between two of them for good, and everything that snaps afterwards -- the
 * cut, the preview seek -- then has to guess which one was meant.
 */
export function stepFrames(sec, frames, fps, duration) {
    const onScreen = Math.floor(Math.max(0, sec) * fps + 1e-6);
    const last = Math.max(0, Math.floor(duration * fps + 1e-6));
    return clamp(onScreen + frames, 0, last) / fps;
}
export function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
}
/**
 * Die Fassung, wie sie in der App steht: `2026.09.05.01.08`.
 *
 * Jahr, Monat, Tag, Stunde, Minute des Baus -- eine Zahl, die von selbst
 * größer wird und die man am Telefon vorlesen kann. Der Commit-Kürzel steht
 * daneben für den, der ihn braucht; für ein Gespräch über „welche Fassung hast
 * du denn?“ taugt er nicht.
 *
 * Gerechnet wird in **deutscher Zeit**, nicht in UTC: die Fassung steht in
 * einer App, die in Deutschland benutzt wird, und wer auf die Uhr sieht, soll
 * dieselbe Stunde lesen. Eine feste Zone (nicht die des Geräts), damit die
 * Fassung trotzdem überall gleich heißt -- Sommer- und Winterzeit rechnet
 * `Intl` von selbst.
 */
export function releaseVersion(builtAt) {
    if (!builtAt)
        return null;
    const at = new Date(builtAt);
    if (Number.isNaN(at.getTime()))
        return null;
    const teile = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(at);
    const teil = (typ) => teile.find((p) => p.type === typ)?.value ?? '00';
    return [teil('year'), teil('month'), teil('day'), teil('hour'), teil('minute')].join('.');
}
/**
 * „vor 3 Minuten“ statt „05.09.26, 14:13“.
 *
 * Auf einer Übersicht will man wissen, was zuletzt dran war — und dafür ist
 * der Abstand die Auskunft, nicht das Datum. Erst ab einer Woche wird wieder
 * das Datum genannt, weil „vor 23 Tagen“ niemand in einen Kalender übersetzt.
 * Die Zeitzone spielt hier keine Rolle: gerechnet wird der Abstand.
 */
export function vorZeit(zeitpunkt, jetzt = new Date()) {
    const dann = zeitpunkt instanceof Date ? zeitpunkt : new Date(zeitpunkt);
    const ms = dann.getTime();
    if (!Number.isFinite(ms))
        return '';
    const sekunden = Math.round((jetzt.getTime() - ms) / 1000);
    // In der Zukunft (Uhren, die auseinanderlaufen) sagen wir lieber nichts
    // Falsches als „vor -2 Minuten“.
    if (sekunden < 45)
        return 'gerade eben';
    const minuten = Math.round(sekunden / 60);
    if (minuten < 60)
        return minuten === 1 ? 'vor einer Minute' : `vor ${minuten} Minuten`;
    const stunden = Math.round(minuten / 60);
    if (stunden < 24)
        return stunden === 1 ? 'vor einer Stunde' : `vor ${stunden} Stunden`;
    const tage = Math.round(stunden / 24);
    if (tage === 1)
        return 'gestern';
    if (tage < 7)
        return `vor ${tage} Tagen`;
    return dann.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
/** „Hoch“, „Quer“ oder „Quadrat“ — das Format in einem Wort. */
export function formatWort(width, height) {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
        return '';
    const verhaeltnis = width / height;
    if (Math.abs(verhaeltnis - 1) < 0.05)
        return 'Quadrat';
    return verhaeltnis > 1 ? 'Quer' : 'Hoch';
}
//# sourceMappingURL=time.js.map